import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

function Header() {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    try {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMenuOpen(false);
      } else {
        console.warn(`Section with id "${sectionId}" not found`);
      }
    } catch (error) {
      console.error('Error scrolling to section:', error);
    }
  };

  return (
    <header className={`fixed w-full top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'nav-scroll' : 'bg-transparent'}`}>
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <h1 className={`text-xl md:text-2xl font-bold transition-colors duration-300 cursor-pointer ${isScrolled ? 'text-blue-600' : 'text-white'}`}>
              <span>{t('hero_title')}</span>
              <p className={`text-xs font-normal mt-1 ${isScrolled ? 'text-gray-600' : 'text-white/90'}`}>{t('hero_subtitle')}</p>
            </h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('intro')}
              className={`transition-colors text-sm font-medium cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white/90 hover:text-white'}`}
            >
              {t('nav_intro')}
            </button>
            <button 
              onClick={() => scrollToSection('business')}
              className={`transition-colors text-sm font-medium cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white/90 hover:text-white'}`}
            >
              {t('nav_business')}
            </button>
            <button 
              onClick={() => scrollToSection('events')}
              className={`transition-colors text-sm font-medium cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white/90 hover:text-white'}`}
            >
              {t('nav_events')}
            </button>
            <button 
              onClick={() => scrollToSection('team')}
              className={`transition-colors text-sm font-medium cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white/90 hover:text-white'}`}
            >
              {t('nav_team')}
            </button>
            <button 
              onClick={() => scrollToSection('achievements')}
              className={`transition-colors text-sm font-medium cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white/90 hover:text-white'}`}
            >
              {t('nav_achievements')}
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className={`transition-colors text-sm font-medium cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white/90 hover:text-white'}`}
            >
              {t('nav_contact')}
            </button>
            <div className={isScrolled ? 'text-gray-700' : 'text-white'}>
              <LanguageSwitcher />
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className={`md:hidden transition-colors ${isScrolled ? 'text-gray-700' : 'text-white'}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <i className={`text-2xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg md:hidden">
            <div className="px-6 py-4 space-y-4">
              <button 
                onClick={() => scrollToSection('intro')}
                className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav_intro')}
              </button>
              <button 
                onClick={() => scrollToSection('business')}
                className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav_business')}
              </button>
              <button 
                onClick={() => scrollToSection('events')}
                className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav_events')}
              </button>
              <button 
                onClick={() => scrollToSection('team')}
                className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav_team')}
              </button>
              <button 
                onClick={() => scrollToSection('achievements')}
                className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav_achievements')}
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                {t('nav_contact')}
              </button>
              <div className="pt-2 border-t border-gray-200">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
